import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import api from '../../services/api';
import useAudioRecording from '../../hooks/useAudioRecording';
import useVideoRecording from '../../hooks/useVideoRecording';
import RecordingSection from '../ExpandedReflectionView/RecordingSection';
import PlaybackSection from '../ExpandedReflectionView/PlaybackSection';
import PreviewSection from '../ExpandedReflectionView/PreviewSection';
import TextSection from '../ExpandedReflectionView/TextSection';
import ImageSection from './ImageSection';
import ProcessingDialog from '../ProcessingDialog';
import './Studio.css';

function Studio({ prompt, token, API_URL, onClose, onReflectionCreated, onRecordingStateChange, getAxiosConfig, transcriptionPreference, lastTranscriptionUpdate, existingReflection, readOnly, parentReflectionId, followupQuestion, authorizeAI, automateTitles, enableFollowups, autoEnhance, lastEnhancementUpdate, lastTitleUpdate, onEnhance, onOpenFollowup, onViewChildReflection, registerBeforeLeave, onDirtyStateChange, onMarkViewed, storageWarning, onNavigateToSettings }) {
  const [activeMode, setActiveMode] = useState(() => {
    if (existingReflection) {
      if (existingReflection.video_url) return 'video';
      if (existingReflection.audio_url) return 'audio';
      if (existingReflection.images?.length > 0) return 'image';
      if (existingReflection.text_content) return 'text';
    }
    return 'video';
  });
  const [reflectionId, setReflectionId] = useState(existingReflection?.id || null);
  const [recordingType, setRecordingType] = useState(null);
  const [isCountingDown, setIsCountingDown] = useState(false);
  const [textContent, setTextContent] = useState(existingReflection?.text_content || '');
  const [images, setImages] = useState(existingReflection?.images || []);
  const [videoUrl, setVideoUrl] = useState(existingReflection?.video_url || null);
  const [audioUrl, setAudioUrl] = useState(existingReflection?.audio_url || null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [error, setError] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [videoTranscriptionStatus, setVideoTranscriptionStatus] = useState(existingReflection?.video_transcription_status || 'none');
  const [videoTranscriptionText, setVideoTranscriptionText] = useState(existingReflection?.video_transcription_text || null);
  const [audioTranscriptionStatus, setAudioTranscriptionStatus] = useState(existingReflection?.audio_transcription_status || 'none');
  const [audioTranscriptionText, setAudioTranscriptionText] = useState(existingReflection?.audio_transcription_text || null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [autoStartType, setAutoStartType] = useState(null);
  const [suggestions, setSuggestions] = useState(existingReflection?.ai_followup_suggestions || null);
  const [enhancementStatus, setEnhancementStatus] = useState(existingReflection?.ai_followup_status || 'none');
  const [showReenhanceConfirm, setShowReenhanceConfirm] = useState(false);
  const [ancestorChain, setAncestorChain] = useState(null);
  const [title, setTitle] = useState(existingReflection?.title || '');
  const [isTitleRegenerating, setIsTitleRegenerating] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [processingText, setProcessingText] = useState(
    existingReflection?.ai_followup_status === 'generating' ? 'Generating follow-up questions...' : null
  );
  const reflectionIdRef = useRef(existingReflection?.id || null);
  const creationPromiseRef = useRef(null);
  const promptDisplayRef = useRef(null);
  const savedTextRef = useRef(existingReflection?.text_content || '');
  const leaveResolverRef = useRef(null);
  const textContentRef = useRef(existingReflection?.text_content || '');
  textContentRef.current = textContent;

  // Effective followup context: from props (new follow-up) or existing reflection (viewing one)
  const effectiveFollowupQuestion = followupQuestion || existingReflection?.followup_question || null;
  const effectiveParentId = parentReflectionId || existingReflection?.parent_reflection_id || null;

  // Fetch ancestor chain when this is a follow-up (new or existing)
  useEffect(() => {
    if (!effectiveParentId) return;
    let cancelled = false;
    api.get(`/reflections/${effectiveParentId}/ancestors`)
      .then(res => {
        if (!cancelled) setAncestorChain(res.data);
      })
      .catch(() => {}); // Silently ignore
    return () => { cancelled = true; };
  }, [effectiveParentId]);

  // Mark shared reflection as viewed when Studio opens
  useEffect(() => {
    if (existingReflection && onMarkViewed) {
      onMarkViewed(existingReflection.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll prompt to 10px from top of viewport on mount
  useEffect(() => {
    if (promptDisplayRef.current) {
      const rect = promptDisplayRef.current.getBoundingClientRect();
      const scrollTarget = window.pageYOffset + rect.top - 10;
      window.scrollTo({ top: Math.max(0, scrollTarget), behavior: 'smooth' });
    }
  }, []);

  // Scroll prompt to top when recording starts (countdown or re-record)
  useEffect(() => {
    if ((isCountingDown || recordingType) && promptDisplayRef.current) {
      const promptTop = promptDisplayRef.current.getBoundingClientRect().top;
      const targetOffset = 10; // 10px from top of visible window
      const distance = promptTop - targetOffset;

      // Skip if already at target position
      if (Math.abs(distance) <= 10) return;

      const duration = 1500; // 1.5 seconds to match mode selector animation
      const startPosition = window.pageYOffset;
      const startTime = performance.now();

      const animateScroll = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Ease-in-out quadratic for smooth animation
        const easeProgress = progress < 0.5
          ? 2 * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;

        window.scrollTo(0, startPosition + (distance * easeProgress));

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };

      requestAnimationFrame(animateScroll);
    }
  }, [isCountingDown, recordingType]);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Lazily create reflection on first content save (video/audio upload or text save)
  const ensureReflection = useCallback(async () => {
    if (reflectionIdRef.current) return reflectionIdRef.current;

    if (!creationPromiseRef.current) {
      const body = { promptId: prompt.id };
      if (parentReflectionId) body.parentReflectionId = parentReflectionId;
      if (followupQuestion) body.followupQuestion = followupQuestion;
      creationPromiseRef.current = api.post(
        '/reflections',
        body
      ).then(response => {
        const id = response.data.reflection.id;
        reflectionIdRef.current = id;
        setReflectionId(id);
        if (onReflectionCreated) onReflectionCreated(id);
        return id;
      }).catch(err => {
        creationPromiseRef.current = null;
        throw err;
      });
    }

    return creationPromiseRef.current;
  }, [prompt.id, onReflectionCreated, parentReflectionId, followupQuestion]);

  // Upload recording blob
  const uploadRecording = useCallback(async (type, blob, durationSeconds) => {
    try {
      const id = await ensureReflection();
      setProcessingText('Saving...');
      setUploadStatus('Saving...');
      const formData = new FormData();
      const extension = blob.type.split('/')[1]?.split(';')[0] || 'webm';
      formData.append(type, blob, `${type}.${extension}`);
      if (durationSeconds != null) {
        formData.append('durationSeconds', durationSeconds);
      }

      const response = await api.put(
        `/upload/reflection/${id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      if (type === 'video') setVideoUrl(response.data.video_url);
      if (type === 'audio') setAudioUrl(response.data.audio_url);

      // Smooth pipeline: if auto-transcribe is on, transition directly to "Transcribing..."
      if (transcriptionPreference === 'always') {
        setProcessingText('Transcribing...');
        if (response.data.video_url) setVideoTranscriptionStatus('processing');
        if (response.data.audio_url) setAudioTranscriptionStatus('processing');
      } else {
        setProcessingText(null);
      }
      setUploadStatus('Saved!');
      setTimeout(() => setUploadStatus(null), 2000);
    } catch (err) {
      console.error('Studio: upload failed', err);
      setProcessingText(null);
      setUploadStatus(null);
      if (err.response?.status === 413) {
        alert('Storage limit reached. Please delete some content or contact your administrator about upgrading your plan.');
      } else {
        alert('Failed to save recording: ' + (err.response?.data?.error || err.message));
      }
    }
  }, [ensureReflection, transcriptionPreference]);

  // Recording hooks
  // Note: We upload first, then clear recordingType, so the video URL is ready
  // before switching from RecordingSection to PlaybackSection (smoother transition)
  // Double rAF ensures layout settles before triggering mode selector expand animation
  const audioRecording = useAudioRecording({
    onComplete: useCallback(async (blob, durationSeconds) => {
      await uploadRecording('audio', blob, durationSeconds);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setRecordingType(null);
        });
      });
    }, [uploadRecording])
  });

  const videoRecording = useVideoRecording({
    onComplete: useCallback(async (blob, durationSeconds) => {
      await uploadRecording('video', blob, durationSeconds);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setRecordingType(null);
        });
      });
    }, [uploadRecording])
  });

  // Propagate recording state for blur effect
  useEffect(() => {
    const shouldBlur = isCountingDown || recordingType !== null;
    if (onRecordingStateChange) {
      onRecordingStateChange(shouldBlur, prompt.id);
    }
  }, [isCountingDown, recordingType, onRecordingStateChange, prompt.id]);

  // Clean up blur on unmount
  useEffect(() => {
    return () => {
      if (onRecordingStateChange) {
        onRecordingStateChange(false, null);
      }
    };
  }, [onRecordingStateChange]);

  // Apply WebSocket transcription updates from App.js
  useEffect(() => {
    if (!lastTranscriptionUpdate || !reflectionId) return;
    if (lastTranscriptionUpdate.id !== reflectionId) return;
    if (lastTranscriptionUpdate.video_transcription_status !== undefined) {
      setVideoTranscriptionStatus(lastTranscriptionUpdate.video_transcription_status);
    }
    if (lastTranscriptionUpdate.video_transcription_text !== undefined) {
      setVideoTranscriptionText(lastTranscriptionUpdate.video_transcription_text);
    }
    if (lastTranscriptionUpdate.audio_transcription_status !== undefined) {
      setAudioTranscriptionStatus(lastTranscriptionUpdate.audio_transcription_status);
    }
    if (lastTranscriptionUpdate.audio_transcription_text !== undefined) {
      setAudioTranscriptionText(lastTranscriptionUpdate.audio_transcription_text);
    }

    // Smooth pipeline: when transcription completes, transition to enhancement or clear
    const videoCompleted = lastTranscriptionUpdate.video_transcription_status === 'completed' || lastTranscriptionUpdate.video_transcription_status === 'failed';
    const audioCompleted = lastTranscriptionUpdate.audio_transcription_status === 'completed' || lastTranscriptionUpdate.audio_transcription_status === 'failed';
    if (videoCompleted || audioCompleted) {
      const hasText = !!(lastTranscriptionUpdate.video_transcription_text?.trim() || lastTranscriptionUpdate.audio_transcription_text?.trim());
      if (hasText && autoEnhance) {
        setProcessingText('Generating follow-up questions...');
      } else {
        setProcessingText(null);
      }
    }
  }, [lastTranscriptionUpdate, reflectionId, autoEnhance]);

  // Apply WebSocket enhancement updates from App.js
  useEffect(() => {
    if (!lastEnhancementUpdate || !reflectionId) return;
    if (lastEnhancementUpdate.id !== reflectionId) return;
    if (lastEnhancementUpdate.ai_followup_status !== undefined) {
      setEnhancementStatus(lastEnhancementUpdate.ai_followup_status);
    }
    if (lastEnhancementUpdate.ai_followup_suggestions !== undefined) {
      setSuggestions(lastEnhancementUpdate.ai_followup_suggestions);
    }
    // Update processing dialog based on enhancement status
    if (lastEnhancementUpdate.ai_followup_status === 'generating') {
      setProcessingText('Generating follow-up questions...');
    } else if (lastEnhancementUpdate.ai_followup_status === 'completed' || lastEnhancementUpdate.ai_followup_status === 'failed') {
      setProcessingText(null);
    }
  }, [lastEnhancementUpdate, reflectionId]);

  // Apply WebSocket title updates from App.js
  useEffect(() => {
    if (!lastTitleUpdate || !reflectionId) return;
    if (lastTitleUpdate.id !== reflectionId) return;
    if (lastTitleUpdate.title) {
      setTitle(lastTitleUpdate.title);
      setIsTitleRegenerating(false);
    }
    if (lastTitleUpdate.generating) {
      setIsTitleRegenerating(true);
    }
  }, [lastTitleUpdate, reflectionId]);

  // Trigger transcription
  const handleTranscribe = useCallback(async () => {
    if (!reflectionId || isTranscribing) return;
    setIsTranscribing(true);
    setProcessingText('Transcribing...');
    try {
      await api.post(
        `/reflections/${reflectionId}/transcribe`,
        {}
      );
      if (videoUrl) setVideoTranscriptionStatus('processing');
      if (audioUrl) setAudioTranscriptionStatus('processing');
    } catch (err) {
      console.error('Studio: transcription failed', err);
      setProcessingText(null);
      alert('Failed to start transcription: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsTranscribing(false);
    }
  }, [reflectionId, isTranscribing, videoUrl, audioUrl]);

  // Save text content
  const saveText = useCallback(async () => {
    if (!textContent) return;
    try {
      const id = await ensureReflection();
      await api.put(
        `/reflections/${id}`,
        { textContent }
      );
      savedTextRef.current = textContent;
      setUploadStatus('Saved!');
      setTimeout(() => setUploadStatus(null), 2000);
    } catch (err) {
      console.error('Studio: text save failed', err);
      alert('Failed to save text: ' + (err.response?.data?.error || err.message));
    }
  }, [ensureReflection, textContent]);

  // Save title
  const saveTitle = useCallback(async (newTitle) => {
    if (!reflectionIdRef.current) return;
    try {
      await api.put(`/reflections/${reflectionIdRef.current}`, { title: newTitle });
    } catch (err) {
      console.error('Studio: title save failed', err);
    }
  }, []);

  // Regenerate title via AI
  const regenerateTitle = useCallback(async () => {
    if (!reflectionIdRef.current || isTitleRegenerating) return;
    setIsTitleRegenerating(true);
    try {
      const res = await api.post(`/reflections/${reflectionIdRef.current}/regenerate-title`, {
        excludeTitle: title || undefined
      });
      if (res.data.title) {
        setTitle(res.data.title);
      }
    } catch (err) {
      console.error('Studio: title regeneration failed', err);
    } finally {
      setIsTitleRegenerating(false);
    }
  }, [title, isTitleRegenerating]);

  // Delete a recording from the reflection
  const deleteRecording = useCallback(async (type) => {
    if (!reflectionId) return;
    try {
      const updateData = type === 'video' ? { videoUrl: null } : { audioUrl: null };
      await api.put(
        `/reflections/${reflectionId}`,
        updateData
      );
      if (type === 'video') {
        setVideoUrl(null);
        setVideoTranscriptionStatus('none');
        setVideoTranscriptionText(null);
      }
      if (type === 'audio') {
        setAudioUrl(null);
        setAudioTranscriptionStatus('none');
        setAudioTranscriptionText(null);
      }
    } catch (err) {
      console.error('Studio: delete recording failed', err);
      alert('Failed to delete recording: ' + (err.response?.data?.error || err.message));
    }
  }, [reflectionId]);

  // Re-record: confirm, delete existing, auto-start countdown
  const handleReRecord = useCallback(async (type) => {
    const mediaType = type === 'video' ? 'video' : 'audio';
    if (!window.confirm(`This will delete your existing ${mediaType} recording and replace it with a new one. Continue?`)) {
      return;
    }
    await deleteRecording(type);
    setAutoStartType(type);
  }, [deleteRecording]);

  const isRecordingActive = isCountingDown || recordingType !== null;

  // Promise-based unsaved changes guard — used by all exit paths
  const checkUnsavedChanges = useCallback(async () => {
    if (readOnly || textContentRef.current === savedTextRef.current) {
      // Clean — save title and resolve immediately
      if (!readOnly && title && reflectionIdRef.current) {
        await saveTitle(title);
      }
      return 'clean';
    }
    // Dirty — show dialog and wait for user choice
    return new Promise((resolve) => {
      leaveResolverRef.current = resolve;
      setShowUnsavedDialog(true);
    });
  }, [readOnly, title, saveTitle]);

  const handleClose = async () => {
    const result = await checkUnsavedChanges();
    if (result === 'cancel') return;
    onClose(reflectionIdRef.current);
  };

  const handleUnsavedSave = async () => {
    setShowUnsavedDialog(false);
    await saveText();
    if (!readOnly && title && reflectionIdRef.current) {
      await saveTitle(title);
    }
    if (leaveResolverRef.current) {
      leaveResolverRef.current('saved');
      leaveResolverRef.current = null;
    }
  };

  const handleUnsavedDiscard = async () => {
    setShowUnsavedDialog(false);
    if (!readOnly && title && reflectionIdRef.current) {
      await saveTitle(title);
    }
    if (leaveResolverRef.current) {
      leaveResolverRef.current('discarded');
      leaveResolverRef.current = null;
    }
  };

  const handleUnsavedCancel = () => {
    setShowUnsavedDialog(false);
    if (leaveResolverRef.current) {
      leaveResolverRef.current('cancel');
      leaveResolverRef.current = null;
    }
  };

  // Register guard with App.js for external navigation
  useEffect(() => {
    if (registerBeforeLeave) registerBeforeLeave(checkUnsavedChanges);
    return () => { if (registerBeforeLeave) registerBeforeLeave(null); };
  }, [registerBeforeLeave, checkUnsavedChanges]);

  // Report dirty state to App.js for popstate handling
  useEffect(() => {
    if (onDirtyStateChange) {
      onDirtyStateChange(!readOnly && textContent !== savedTextRef.current);
    }
  }, [textContent, readOnly, onDirtyStateChange]);

  if (error) {
    return (
      <div className="studio-container">
        <div className="studio-error">{error}</div>
        <div className="studio-actions">
          <button className="studio-back-btn" onClick={onClose}>Back to Prompts</button>
        </div>
      </div>
    );
  }

  return (
    <div className="studio-container">
      {storageWarning && (
        <div className="studio-storage-warning">{storageWarning}</div>
      )}
      {effectiveFollowupQuestion ? (
        <div className="studio-chain" ref={promptDisplayRef}>
          {/* Prompt box (smallest, most subdued) */}
          <div className="studio-chain-node studio-chain-prompt">
            {prompt.prompt_text}
          </div>
          <div className="studio-chain-connector" />

          {/* Ancestor follow-up questions (small, subdued) */}
          {ancestorChain && ancestorChain.map((ancestor) => (
            ancestor.followup_question && (
              <React.Fragment key={ancestor.id}>
                <div className="studio-chain-node studio-chain-ancestor">
                  {ancestor.followup_question}
                </div>
                <div className="studio-chain-connector" />
              </React.Fragment>
            )
          ))}

          {/* Current question being answered (large, prominent) */}
          <div className={`studio-chain-node studio-chain-current${effectiveFollowupQuestion.length > 100 ? ' wide' : ''}`}>
            {effectiveFollowupQuestion}
          </div>
        </div>
      ) : (
        <div className="studio-prompt-display" ref={promptDisplayRef}>
          {prompt.prompt_text}
          {prompt.created_by_username && (
            <div className="studio-prompt-attribution">
              💌 Prompt suggested by {prompt.created_by_username}
            </div>
          )}
        </div>
      )}

      {/* Title field - collapses during recording */}
      {!readOnly && reflectionId && (
        <div className={`studio-title-row ${isRecordingActive ? 'collapsed' : ''}`}>
          <label className="studio-title-label">Title</label>
          <input
            type="text"
            className="studio-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => saveTitle(title)}
            placeholder="Untitled reflection"
            maxLength={80}
            disabled={isRecordingActive}
          />
          {authorizeAI && automateTitles && (
            <button
              className="studio-title-refresh-btn"
              onClick={regenerateTitle}
              disabled={isTitleRegenerating || isRecordingActive || !(textContent || videoTranscriptionText || audioTranscriptionText)}
              title={isTitleRegenerating ? 'Generating...' : 'Regenerate title with AI'}
            >
              {isTitleRegenerating ? '...' : '\u21BB'}
            </button>
          )}
        </div>
      )}
      {readOnly && existingReflection?.title && (
        <div className="studio-title-row readonly">
          <label className="studio-title-label">Title:</label>
          <div className="studio-title-display">{existingReflection.title}</div>
        </div>
      )}

      <div className={`studio-mode-selector ${isRecordingActive ? 'collapsed' : ''}`}>
        {(!readOnly || videoUrl) && (
          <button
            className={`studio-mode-btn ${activeMode === 'video' ? 'active' : ''}`}
            onClick={() => setActiveMode('video')}
            disabled={isRecordingActive}
          >
            Video
          </button>
        )}
        {(!readOnly || audioUrl) && (
          <button
            className={`studio-mode-btn ${activeMode === 'audio' ? 'active' : ''}`}
            onClick={() => setActiveMode('audio')}
            disabled={isRecordingActive}
          >
            Audio
          </button>
        )}
        {(!readOnly || images.length > 0) && (
          <button
            className={`studio-mode-btn ${activeMode === 'image' ? 'active' : ''}`}
            onClick={() => setActiveMode('image')}
            disabled={isRecordingActive}
          >
            Image
          </button>
        )}
        {(!readOnly || textContent) && (
          <button
            className={`studio-mode-btn ${activeMode === 'text' ? 'active' : ''}`}
            onClick={() => { if (activeMode === 'text') return; setActiveMode('text'); }}
            disabled={isRecordingActive}
          >
            Text
          </button>
        )}
      </div>

      <div className="studio-recording-area">
        {activeMode === 'video' && (
          readOnly ? (
            videoUrl ? (
              <PlaybackSection
                type="video"
                url={videoUrl}
                reflectionId={reflectionId}
                transcriptionStatus={videoTranscriptionStatus}
                transcriptionText={videoTranscriptionText}
              />
            ) : (
              <p className="no-content">No video recorded</p>
            )
          ) : recordingType === 'video' ? (
            <RecordingSection
              type="video"
              recording={videoRecording}
              formatDuration={formatDuration}
              promptText={null}
              disableAutoScroll
            />
          ) : videoUrl ? (
            <div>
              <PlaybackSection
                type="video"
                url={videoUrl}
                reflectionId={reflectionId}
                transcriptionStatus={videoTranscriptionStatus}
                transcriptionText={videoTranscriptionText}
                transcriptionPreference={transcriptionPreference}
                onNavigateToSettings={onNavigateToSettings}
              />
              <div className="studio-playback-actions">
                {transcriptionPreference === 'on_demand' && (videoTranscriptionStatus === 'none' || videoTranscriptionStatus === 'failed') && (
                  <button
                    className="studio-action-btn studio-transcribe-btn"
                    onClick={handleTranscribe}
                    disabled={isTranscribing}
                  >
                    Transcribe
                  </button>
                )}
                <button
                  className="studio-action-btn studio-rerecord-btn"
                  onClick={() => handleReRecord('video')}
                >
                  Re-record
                </button>
                <button
                  className="studio-action-btn studio-delete-btn"
                  onClick={() => deleteRecording('video')}
                >
                  Delete
                </button>
              </div>
            </div>
          ) : (
            <PreviewSection
              type="video"
              onStartRecording={(stream) => {
                setIsCountingDown(false); // Reset before PreviewSection unmounts
                setRecordingType('video');
                setAutoStartType(null);
                videoRecording.start(stream);
              }}
              onCountdownChange={setIsCountingDown}
              promptText={null}
              disableAutoScroll
              autoStart={autoStartType === 'video'}
            />
          )
        )}

        {activeMode === 'audio' && (
          readOnly ? (
            audioUrl ? (
              <PlaybackSection
                type="audio"
                url={audioUrl}
                onAudioStateChange={setAudioPlaying}
                isPlaying={audioPlaying}
                reflectionId={reflectionId}
                transcriptionStatus={audioTranscriptionStatus}
                transcriptionText={audioTranscriptionText}
              />
            ) : (
              <p className="no-content">No audio recorded</p>
            )
          ) : recordingType === 'audio' ? (
            <RecordingSection
              type="audio"
              recording={audioRecording}
              formatDuration={formatDuration}
              promptText={null}
              disableAutoScroll
            />
          ) : audioUrl ? (
            <div>
              <PlaybackSection
                type="audio"
                url={audioUrl}
                onAudioStateChange={setAudioPlaying}
                isPlaying={audioPlaying}
                reflectionId={reflectionId}
                transcriptionStatus={audioTranscriptionStatus}
                transcriptionText={audioTranscriptionText}
                transcriptionPreference={transcriptionPreference}
                onNavigateToSettings={onNavigateToSettings}
              />
              <div className="studio-playback-actions">
                {transcriptionPreference === 'on_demand' && (audioTranscriptionStatus === 'none' || audioTranscriptionStatus === 'failed') && (
                  <button
                    className="studio-action-btn studio-transcribe-btn"
                    onClick={handleTranscribe}
                    disabled={isTranscribing}
                  >
                    Transcribe
                  </button>
                )}
                <button
                  className="studio-action-btn studio-rerecord-btn"
                  onClick={() => handleReRecord('audio')}
                >
                  Re-record
                </button>
                <button
                  className="studio-action-btn studio-delete-btn"
                  onClick={() => deleteRecording('audio')}
                >
                  Delete
                </button>
              </div>
            </div>
          ) : (
            <PreviewSection
              type="audio"
              onStartRecording={(stream) => {
                setIsCountingDown(false); // Reset before PreviewSection unmounts
                setRecordingType('audio');
                setAutoStartType(null);
                audioRecording.start(stream);
              }}
              onCountdownChange={setIsCountingDown}
              promptText={null}
              disableAutoScroll
              autoStart={autoStartType === 'audio'}
            />
          )
        )}

        {activeMode === 'image' && (
          <ImageSection
            reflectionId={reflectionId}
            images={images}
            onImagesChange={setImages}
            readOnly={readOnly}
            ensureReflection={ensureReflection}
          />
        )}

        {activeMode === 'text' && (
          <TextSection
            isEditMode={!readOnly}
            textContent={textContent}
            isActive={true}
            onToggle={() => {}}
            onFieldChange={(field, value) => setTextContent(value)}
            isDisabled={false}
            isRecordingActive={false}
            hideHeader
          />
        )}

        {!readOnly && activeMode === 'text' && textContent && (
          <button className="studio-save-changes-btn" onClick={saveText}>
            Save Changes
          </button>
        )}
      </div>

      {uploadStatus && uploadStatus !== 'Saving...' && (
        <div className="studio-status">{uploadStatus}</div>
      )}

      {/* Enhancement Section - hide entirely when enableFollowups is off and no answered suggestions */}
      {!readOnly && authorizeAI && reflectionId && (textContent || videoTranscriptionText || audioTranscriptionText) &&
       (enableFollowups || suggestions?.some(s => s.answered)) && (
        <div className="studio-enhance-section">
          {enableFollowups && (
            <>
              {enhancementStatus !== 'generating' && (
                <button
                  className="studio-action-btn studio-enhance-btn"
                  onClick={() => {
                    if (suggestions?.length > 0) {
                      setShowReenhanceConfirm(true);
                    } else {
                      onEnhance(reflectionId);
                    }
                  }}
                >
                  {suggestions?.length > 0 ? 'New Questions' : 'Generate Followup Questions'}
                </button>
              )}

              {showReenhanceConfirm && (
                <div className="studio-reenhance-confirm">
                  <p>Existing unanswered questions will be replaced. Questions you've already responded to will be kept.</p>
                  <div className="studio-reenhance-actions">
                    <button className="studio-action-btn" onClick={() => { setShowReenhanceConfirm(false); onEnhance(reflectionId); }}>OK</button>
                    <button className="studio-action-btn" onClick={() => setShowReenhanceConfirm(false)}>Cancel</button>
                  </div>
                </div>
              )}
            </>
          )}

          {(() => {
            const displaySuggestions = enableFollowups
              ? suggestions
              : suggestions?.filter(s => s.answered);
            return displaySuggestions?.length > 0 && (
              <div className="studio-suggestions-panel">
                {displaySuggestions.map((s, i) => (
                  <div key={i} className={`studio-suggestion ${s.answered ? 'answered' : ''}`}>
                    <span className="studio-suggestion-text">{s.question}</span>
                    {s.answered ? (
                      <button
                        className="studio-action-btn studio-suggestion-respond-btn"
                        onClick={async () => {
                          const result = await checkUnsavedChanges();
                          if (result === 'cancel') return;
                          onViewChildReflection && onViewChildReflection(s.child_reflection_id);
                        }}
                      >
                        Show Answer
                      </button>
                    ) : (
                      enableFollowups && onOpenFollowup && (
                        <button
                          className="studio-action-btn studio-suggestion-respond-btn"
                          onClick={async () => {
                            const result = await checkUnsavedChanges();
                            if (result === 'cancel') return;
                            onOpenFollowup(reflectionId, s.question);
                          }}
                        >
                          Respond
                        </button>
                      )
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      <div className="studio-actions">
        <button className="studio-back-btn" onClick={handleClose} disabled={isRecordingActive}>
          Back to Prompts
        </button>
      </div>

      <ProcessingDialog
        show={!!processingText}
        text={processingText || ''}
      />

      {showUnsavedDialog && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={handleUnsavedCancel}>
          <div className="studio-unsaved-dialog" onClick={(e) => e.stopPropagation()}>
            <h2>Unsaved Changes</h2>
            <p>You have unsaved text changes. Would you like to save before leaving?</p>
            <div className="studio-unsaved-actions">
              <button className="studio-unsaved-btn studio-unsaved-btn-save" onClick={handleUnsavedSave}>Save Changes</button>
              <button className="studio-unsaved-btn studio-unsaved-btn-discard" onClick={handleUnsavedDiscard}>Don't Save</button>
              <button className="studio-unsaved-btn studio-unsaved-btn-cancel" onClick={handleUnsavedCancel}>Cancel</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default Studio;
